import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Wallet, Crown, AlertTriangle, Clock, CheckCircle, Plus, 
  CreditCard, RefreshCw, Phone, User, Calendar, IndianRupee,
  AlertCircle, History
} from "lucide-react";

export const AdminPaymentsPage = () => {
  const [admins, setAdmins] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  
  // Modal states
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [showAddCashModal, setShowAddCashModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [adminHistory, setAdminHistory] = useState({ admin: null, transactions: [] });
  
  // Form states
  const [cashAmount, setCashAmount] = useState(1000);
  const [cashNotes, setCashNotes] = useState("");
  const [cashPaymentMode, setCashPaymentMode] = useState("cash");
  const [subPlan, setSubPlan] = useState("yearly");
  const [subAmount, setSubAmount] = useState(7999);
  const [subNotes, setSubNotes] = useState("");
  const [subPaymentMode, setSubPaymentMode] = useState("cash");
  const [processing, setProcessing] = useState(false);

  const planPrices = {
    monthly: 999,
    quarterly: 2499,
    half_yearly: 4499,
    yearly: 7999
  };

  useEffect(() => {
    fetchAdmins();
  }, [filter]);

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/superadmin/admins/payments-overview?status_filter=${filter}`);
      setAdmins(res.data.admins);
      setSummary(res.data.summary);
    } catch (err) {
      toast.error("Failed to fetch admin data");
    } finally {
      setLoading(false);
    }
  };

  const handleAddCash = async () => {
    if (cashAmount <= 0) {
      toast.error("Amount must be greater than 0");
      return;
    }
    setProcessing(true);
    try {
      const res = await axios.post(
        `${API}/superadmin/admins/${selectedAdmin.id}/wallet/add-cash?amount=${cashAmount}&payment_mode=${cashPaymentMode}${cashNotes ? `&notes=${encodeURIComponent(cashNotes)}` : ''}`
      );
      toast.success(res.data.message);
      setShowAddCashModal(false);
      setCashAmount(1000);
      setCashNotes("");
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add cash");
    } finally {
      setProcessing(false);
    }
  };

  const handleRecordSubscription = async () => {
    if (subAmount <= 0) {
      toast.error("Amount must be greater than 0");
      return;
    }
    setProcessing(true);
    try {
      const res = await axios.post(
        `${API}/superadmin/admins/${selectedAdmin.id}/subscription/record-payment?plan=${subPlan}&amount=${subAmount}&payment_mode=${subPaymentMode}${subNotes ? `&notes=${encodeURIComponent(subNotes)}` : ''}`
      );
      toast.success(res.data.message);
      setShowSubscriptionModal(false);
      setSubAmount(planPrices.yearly);
      setSubPlan("yearly");
      setSubNotes("");
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record payment");
    } finally {
      setProcessing(false);
    }
  };

  const fetchAdminHistory = async (admin) => {
    try {
      const res = await axios.get(`${API}/superadmin/admins/${admin.id}/payment-history`);
      setAdminHistory(res.data);
      setShowHistoryModal(true);
    } catch (err) {
      toast.error("Failed to fetch payment history");
    }
  };

  const openAddCashModal = (admin) => {
    setSelectedAdmin(admin);
    setCashAmount(1000);
    setCashNotes("");
    setShowAddCashModal(true);
  };

  const openSubscriptionModal = (admin) => {
    setSelectedAdmin(admin);
    setSubPlan("yearly");
    setSubAmount(planPrices.yearly);
    setSubNotes("");
    setShowSubscriptionModal(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      trial: "bg-blue-100 text-blue-700",
      trial_expired: "bg-red-100 text-red-700",
      active: "bg-green-100 text-green-700",
      expired: "bg-orange-100 text-orange-700",
    };
    const labels = {
      trial: "Trial",
      trial_expired: "Trial Expired",
      active: "Active",
      expired: "Expired",
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || "bg-gray-100 text-gray-700"}`}>
        {labels[status] || status}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString('en-IN', { 
      day: 'numeric', month: 'short', year: 'numeric' 
    });
  };

  return (
    <div data-testid="admin-payments-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Admin Payments</h1>
          <p className="text-muted-foreground">Manage wallet deposits and subscription payments</p>
        </div>
        <Button variant="outline" onClick={fetchAdmins} data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <User className="w-5 h-5 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Total Admins</span>
          </div>
          <p className="text-2xl font-bold">{summary.total || 0}</p>
        </div>
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-5 h-5 text-blue-500" />
            <span className="text-sm text-muted-foreground">On Trial</span>
          </div>
          <p className="text-2xl font-bold text-blue-600">{summary.trial || 0}</p>
        </div>
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <span className="text-sm text-muted-foreground">Trial Expired</span>
          </div>
          <p className="text-2xl font-bold text-red-600">{summary.trial_expired || 0}</p>
        </div>
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span className="text-sm text-muted-foreground">Active</span>
          </div>
          <p className="text-2xl font-bold text-green-600">{summary.active || 0}</p>
        </div>
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-5 h-5 text-orange-500" />
            <span className="text-sm text-muted-foreground">Needs Attention</span>
          </div>
          <p className="text-2xl font-bold text-orange-600">{summary.needs_attention || 0}</p>
        </div>
      </div>

      {/* Filter Tabs */}
      <Tabs value={filter} onValueChange={setFilter} className="mb-4">
        <TabsList>
          <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
          <TabsTrigger value="needs_attention" data-testid="filter-needs-attention">
            Needs Attention ({summary.needs_attention || 0})
          </TabsTrigger>
          <TabsTrigger value="trial" data-testid="filter-trial">Trial</TabsTrigger>
          <TabsTrigger value="trial_expired" data-testid="filter-trial-expired">Trial Expired</TabsTrigger>
          <TabsTrigger value="active" data-testid="filter-active">Active</TabsTrigger>
          <TabsTrigger value="expired" data-testid="filter-expired">Expired</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Admins Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : admins.length === 0 ? (
        <div className="bg-card rounded-xl border border-border p-8 text-center">
          <User className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
          <p className="text-muted-foreground">No admins found</p>
        </div>
      ) : (
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Admin</TableHead>
                <TableHead>Wallet Balance</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Days Left</TableHead>
                <TableHead>Expiry</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {admins.map((admin) => (
                <TableRow key={admin.id} className={admin.status_category === "trial_expired" || admin.status_category === "expired" ? "bg-red-50/50" : ""}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{admin.name}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {admin.phone}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={`font-semibold ${admin.wallet_balance <= 0 ? "text-red-600" : admin.wallet_balance < 500 ? "text-orange-600" : "text-green-600"}`}>
                      ₹{admin.wallet_balance?.toLocaleString('en-IN') || 0}
                    </span>
                  </TableCell>
                  <TableCell>{getStatusBadge(admin.status_category)}</TableCell>
                  <TableCell>
                    {admin.subscription_plan ? (
                      <span className="capitalize">{admin.subscription_plan.replace('_', ' ')}</span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={`font-medium ${admin.days_remaining <= 3 ? "text-red-600" : admin.days_remaining <= 7 ? "text-orange-600" : ""}`}>
                      {admin.days_remaining} days
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {formatDate(admin.expiry_date)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openAddCashModal(admin)}
                        data-testid={`add-cash-${admin.id}`}
                      >
                        <Wallet className="w-4 h-4 mr-1" />
                        Add Cash
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => openSubscriptionModal(admin)}
                        data-testid={`record-sub-${admin.id}`}
                      >
                        <Crown className="w-4 h-4 mr-1" />
                        Subscription
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => fetchAdminHistory(admin)}
                        data-testid={`view-history-${admin.id}`}
                      >
                        <History className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Add Cash Modal */}
      <Dialog open={showAddCashModal} onOpenChange={setShowAddCashModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-green-600" />
              Add Cash to Wallet
            </DialogTitle>
            <DialogDescription>
              Record cash payment received from {selectedAdmin?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-muted p-3 rounded-lg">
              <p className="text-sm"><strong>Admin:</strong> {selectedAdmin?.name}</p>
              <p className="text-sm"><strong>Phone:</strong> {selectedAdmin?.phone}</p>
              <p className="text-sm"><strong>Current Balance:</strong> ₹{selectedAdmin?.wallet_balance?.toLocaleString('en-IN') || 0}</p>
            </div>
            
            <div>
              <Label>Amount (₹)</Label>
              <Input
                type="number"
                min="1"
                value={cashAmount}
                onChange={(e) => setCashAmount(parseFloat(e.target.value) || 0)}
                placeholder="Enter amount"
              />
            </div>
            
            <div className="flex gap-2">
              {[500, 1000, 2000, 5000].map((amt) => (
                <Button
                  key={amt}
                  variant={cashAmount === amt ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCashAmount(amt)}
                >
                  ₹{amt}
                </Button>
              ))}
            </div>
            
            <div>
              <Label>Payment Mode</Label>
              <Select value={cashPaymentMode} onValueChange={setCashPaymentMode}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={cashNotes}
                onChange={(e) => setCashNotes(e.target.value)}
                placeholder="Any additional notes..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCashModal(false)}>Cancel</Button>
            <Button onClick={handleAddCash} disabled={processing || cashAmount <= 0}>
              {processing ? "Processing..." : `Add ₹${cashAmount.toLocaleString('en-IN')}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subscription Payment Modal */}
      <Dialog open={showSubscriptionModal} onOpenChange={setShowSubscriptionModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-500" />
              Record Subscription Payment
            </DialogTitle>
            <DialogDescription>
              Record subscription payment received from {selectedAdmin?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-muted p-3 rounded-lg">
              <p className="text-sm"><strong>Admin:</strong> {selectedAdmin?.name}</p>
              <p className="text-sm"><strong>Phone:</strong> {selectedAdmin?.phone}</p>
              <p className="text-sm"><strong>Current Status:</strong> {getStatusBadge(selectedAdmin?.status_category)}</p>
            </div>
            
            <div>
              <Label>Subscription Plan</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {[
                  { id: "monthly", label: "Monthly", price: 999, days: 30 },
                  { id: "quarterly", label: "Quarterly", price: 2499, days: 90 },
                  { id: "half_yearly", label: "Half Yearly", price: 4499, days: 180 },
                  { id: "yearly", label: "Yearly", price: 7999, days: 365 },
                ].map((plan) => (
                  <button
                    key={plan.id}
                    onClick={() => { setSubPlan(plan.id); setSubAmount(plan.price); }}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      subPlan === plan.id
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50"
                    }`}
                  >
                    <p className="font-medium">{plan.label}</p>
                    <p className="text-lg font-bold">₹{plan.price.toLocaleString('en-IN')}</p>
                    <p className="text-xs text-muted-foreground">{plan.days} days</p>
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <Label>Actual Amount Received (₹)</Label>
              <Input
                type="number"
                min="1"
                value={subAmount}
                onChange={(e) => setSubAmount(parseFloat(e.target.value) || 0)}
                placeholder="Enter amount received"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Standard price: ₹{planPrices[subPlan]?.toLocaleString('en-IN')}
              </p>
            </div>
            
            <div>
              <Label>Payment Mode</Label>
              <Select value={subPaymentMode} onValueChange={setSubPaymentMode}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={subNotes}
                onChange={(e) => setSubNotes(e.target.value)}
                placeholder="Any additional notes..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubscriptionModal(false)}>Cancel</Button>
            <Button onClick={handleRecordSubscription} disabled={processing || subAmount <= 0}>
              {processing ? "Processing..." : `Record ₹${subAmount.toLocaleString('en-IN')} Payment`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment History Modal */}
      <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Payment History - {adminHistory.admin?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {adminHistory.admin && (
              <div className="bg-muted p-3 rounded-lg mb-4">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Phone</p>
                    <p className="font-medium">{adminHistory.admin.phone}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Wallet Balance</p>
                    <p className="font-medium">₹{adminHistory.admin.wallet_balance?.toLocaleString('en-IN')}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Subscription</p>
                    <p className="font-medium capitalize">{adminHistory.admin.subscription_plan || adminHistory.admin.subscription_status}</p>
                  </div>
                </div>
              </div>
            )}
            
            {adminHistory.transactions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No payment history</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {adminHistory.transactions.map((tx) => (
                    <TableRow key={tx.id}>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs ${
                          tx.type.includes('subscription') ? 'bg-purple-100 text-purple-700' :
                          tx.type === 'cash_deposit' ? 'bg-green-100 text-green-700' :
                          tx.type === 'recharge' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {tx.type.replace('_', ' ')}
                        </span>
                      </TableCell>
                      <TableCell className="max-w-xs truncate">{tx.description}</TableCell>
                      <TableCell className="font-semibold">₹{tx.amount?.toLocaleString('en-IN')}</TableCell>
                      <TableCell className="capitalize">{tx.payment_mode || tx.payment_method || '-'}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatDate(tx.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPaymentsPage;
